import { Static, Type } from '@sinclair/typebox';
import { Admin360TrialRelanceSchema } from './trial-relance/trial-relance.admin.schema';

// =====================================================================
// CRM 360 — spine d'événements (lifecycle_event) + fiche 360
// cf. admin-cleanup-and-vision-plan §5. Enums dual-defined avec schema.prisma.
// =====================================================================

export enum ConversationPilot {
  BOT = 'BOT',
  HUMAN = 'HUMAN',
}

export enum LifecycleEventFamily {
  TRANSITION = 'TRANSITION',
  ACTION = 'ACTION',
  SIGNAL = 'SIGNAL',
}

export enum LifecycleEventSource {
  PULSE = 'PULSE',
  CRON = 'CRON',
  WEBHOOK = 'WEBHOOK',
  ADMIN = 'ADMIN',
  MIGRATION = 'MIGRATION',
  SYSTEM = 'SYSTEM',
}

// Catalogue des `type` connus (le champ DB reste une string libre pour rester souple).
export const LIFECYCLE_EVENT_TYPE = {
  SIGNUP_COMPLETED: 'signup_completed',
  BECAME_PAID: 'became_paid',
  PLAN_UPGRADED: 'plan_upgraded',
  BECAME_AT_RISK: 'became_at_risk',
  CHURNED: 'churned',
  MIGRATED_FROM_CFIXE: 'migrated_from_cfixe',
  HANDOFF_TAKEOVER: 'handoff_takeover',
  /** L'admin rend la conversation à Pulse — symétrique de `handoff_takeover`. */
  HANDOFF_RELEASE: 'handoff_release',
  /** Pulse passe la main à l'équipe (outil `escalate_human`) — alimente l'inbox d'escalade. */
  ESCALATED: 'escalated',
  /** Contexte du lead remis à zéro depuis le back-office (conversations, relances, qualification). */
  LEAD_RESET: 'lead_reset',
  // Relance des essais (ACTION / SIGNAL) — payload { moment, voie, stage, angle, segment,
  // channels, content_key, notification_id } ; cf. trial-relance-plan §6.
  NUDGE_SENT: 'nudge_sent',
  NUDGE_OPENED: 'nudge_opened',
  NUDGE_CLICKED: 'nudge_clicked',
  NUDGE_FAILED: 'nudge_failed',
  SCORE_CHANGED: 'score_changed',
  STAGE_REACHED: 'stage_reached',
  SEGMENT_CHANGED: 'segment_changed',
  // CRM, suivi humain (registre de contact, file d'appels, formation).
  CONTACT_REFUSED: 'contact_refused',
  CONTACT_REFUSAL_LIFTED: 'contact_refusal_lifted',
  QUEUE_ENTERED: 'queue_entered',
  CALL_LOGGED: 'call_logged',
  QUEUE_EXITED: 'queue_exited',
  TRAINING_ENROLLED: 'training_enrolled',
  TRAINING_ATTENDED: 'training_attended',
  TRAINING_MISSED: 'training_missed',
  DEMO_ATTENDED: 'demo_attended',
  DEMO_MISSED: 'demo_missed',
} as const;

export type LifecycleEventType = (typeof LIFECYCLE_EVENT_TYPE)[keyof typeof LIFECYCLE_EVENT_TYPE];

// ── Fiche 360 (GET /app/admin/crm/:professional_id/360) — lecture seule ──────────

/** Un point de la timeline unifiée (lifecycle_event + billing_event + audit). */
export const Admin360TimelineItemSchema = Type.Object({
  source_kind: Type.String(), // 'lifecycle' | 'billing' | 'audit'
  family: Type.Optional(Type.String()),
  type: Type.String(),
  label: Type.String(),
  occurred_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'occurred_at', with_time: true } }),
});

export const Admin360Schema = Type.Object({
  // Identité pro
  professional_id: Type.Number(),
  name: Type.String(),
  email_contact: Type.Optional(Type.String()),
  phone_number: Type.Optional(Type.String()),
  // `phone_number` est en saisie libre (import v1) : numéro à composer, absent s'il n'est pas lisible sans doute.
  phone_e164: Type.Optional(Type.String()),
  is_migrated: Type.Boolean(),
  acquired_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'acquired_at' } })),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at' } }),
  // Lead lié (via la FK pivot)
  lead_id: Type.Optional(Type.Number()),
  lead_state: Type.Optional(Type.String()),
  lead_scoring: Type.Optional(Type.Number()),
  lead_source: Type.Optional(Type.String()),
  // Abonnement courant
  subscription_status: Type.Optional(Type.String()),
  subscription_label: Type.Optional(Type.String()),
  subscription_amount_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'subscription_amount_minor' } }),
  ),
  payment_at_risk: Type.Optional(Type.Boolean()),
  // Compteurs plateforme
  invoices_count: Type.Number(),
  customers_count: Type.Number(),
  // Relance d'essai (présent seulement pendant l'essai) — état de la séquence en tête de fiche.
  trial_relance: Type.Optional(Admin360TrialRelanceSchema),
  // Timeline unifiée (triée desc)
  timeline: Type.Array(Admin360TimelineItemSchema),
});

export type Admin360TimelineItem = Static<typeof Admin360TimelineItemSchema>;
export type Admin360 = Static<typeof Admin360Schema>;

// ── Factures d'un pro & remboursements (admin) ───────────────────────────────────

/**
 * Pourquoi une facture n'est pas remboursable depuis l'admin. `null` ⇒ elle l'est.
 * Le front s'en sert pour afficher la marche à suivre au lieu d'un bouton inopérant.
 */
export enum AdminInvoiceRefundBlocker {
  /** Facture émise chez un provider incapable de produire un avoir (héritage Stripe). */
  PROVIDER_NOT_SUPPORTED = 'PROVIDER_NOT_SUPPORTED',
  /** Pas de référence de paiement stockée : impossible de savoir quoi rembourser. */
  PAYMENT_REF_MISSING = 'PAYMENT_REF_MISSING',
  /** Facture non émise (échec ou en cours) — rien à annuler. */
  NOT_EMITTED = 'NOT_EMITTED',
  /** Déjà intégralement remboursée. */
  FULLY_REFUNDED = 'FULLY_REFUNDED',
  /** La ligne est elle-même un avoir. */
  IS_CREDIT_NOTE = 'IS_CREDIT_NOTE',
  /**
   * Réglée par prélèvement SEPA (GoCardless, référence `PM…`) : le remboursement automatique ne parle
   * qu'à Stripe — à rembourser depuis GoCardless, puis avoir à créer.
   */
  PAID_BY_SEPA_DEBIT = 'PAID_BY_SEPA_DEBIT',
}

export const AdminInvoiceSchema = Type.Object({
  invoice_id: Type.Number(),
  provider: Type.String(),
  provider_invoice_number: Type.Optional(Type.String()),
  status: Type.String(),
  label: Type.Optional(Type.String()),
  amount_all_taxes_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'amount_all_taxes_minor' } }),
  ),
  /** Montant encore remboursable, TTC (0 si totalement remboursée). */
  refundable_amount_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'refundable_amount_minor' } }),
  ),
  /** Valeur brute en centimes — le front en a besoin pour borner la saisie d'un remboursement partiel. */
  refundable_amount_minor: Type.Number(),
  currency: Type.String(),
  pdf_url: Type.Optional(Type.String()),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
  /** true quand la ligne est un avoir (montants négatifs) rattaché à une facture. */
  is_credit_note: Type.Boolean(),
  credited_invoice_id: Type.Optional(Type.Number()),
  refund_reason: Type.Optional(Type.String()),
  /** `null` ⇒ remboursable. Sinon, raison du blocage à expliquer à l'admin. */
  refund_blocker: Type.Optional(Type.Enum(AdminInvoiceRefundBlocker)),
});

export const AdminInvoiceListSchema = Type.Object({
  items: Type.Array(AdminInvoiceSchema),
});

export const AdminRefundInvoiceBodySchema = Type.Object({
  /**
   * Montant TTC à rembourser en centimes. Omis ⇒ remboursement total du restant dû.
   * Le montant réellement remboursé est recalculé depuis le HT de l'avoir, pour que
   * l'argent rendu et le document comptable coïncident au centime près.
   */
  amount_all_taxes_minor: Type.Optional(Type.Number({ minimum: 1 })),
  reason: Type.String({ minLength: 3, maxLength: 1000 }),
});

export const AdminRefundInvoiceResponseSchema = Type.Object({
  credit_note_invoice_id: Type.Number(),
  refunded_amount_formatted: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'refunded_amount_all_taxes_minor' } }),
  ),
  provider_invoice_number: Type.Optional(Type.String()),
  /** true ⇒ argent rendu mais avoir non encore émis (reprise automatique programmée). */
  credit_note_pending: Type.Boolean(),
});

export type AdminInvoice = Static<typeof AdminInvoiceSchema>;
export type AdminInvoiceList = Static<typeof AdminInvoiceListSchema>;
export type AdminRefundInvoiceBody = Static<typeof AdminRefundInvoiceBodySchema>;
export type AdminRefundInvoiceResponse = Static<typeof AdminRefundInvoiceResponseSchema>;
